import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { Chip, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { FC, Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ConversionGoal, GetConversionGoalDocument, GetTagsDocument, Tag } from '../../generated/graphql';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import getDeviceType from '../../utility/getDeviceType';
import getGoalType from '../../utility/getGoalType';
import getMethodType from '../../utility/getMethodType';
import Loading from '../component/compound/Loading';
import { PageContainer, PageHeader, PageTitle, PaperButton } from '../component/primitive/Page';
import {
  PaperContent,
  PaperDivider,
  PaperFooter,
  PaperItem,
  PaperTitle,
  PaperTitleText,
  StyledPaper,
} from '../component/primitive/Paper';
import GoalSettingEditDrawer from './GoalSettingEditDrawer';

type Props = {
  className?: string;
};

type Params = {
  id: string;
};

type SelectedTag = {
  tag: Tag;
  selected: boolean;
};

type DrawerType = 'NAME' | 'TAG' | 'URL' | 'EVENT';

const GoalSettingDetailPage: FC<Props> = ({ className }) => {
  const { id: goalId } = useParams<Params>();
  const [open, setOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>();
  const [drawerType, setDrawerType] = useState<DrawerType>('NAME');

  const { data, refetch, loading } = useQuery<{ conversionGoal: ConversionGoal }>(GetConversionGoalDocument, {
    variables: { id: goalId },
    context: { clientName: 'planbcd' },
  });

  const { data: tagsData } = useQuery<{ tags: Tag[] }>(GetTagsDocument, {
    context: { clientName: 'planbcd' },
  });

  useEffect(() => {
    if (tagsData) {
      const list = tagsData.tags.map((tag) => {
        return {
          tag: tag,
          selected: false,
        };
      });

      setSelectedTags(list);
    }
  }, [tagsData]);

  return (
    <div className={className}>
      {data && selectedTags ? (
        <GoalSettingEditDrawer
          goal={data.conversionGoal}
          tags={selectedTags}
          open={open}
          refetch={refetch}
          setOpen={setOpen}
          drawerType={drawerType}
        />
      ) : null}
      {!loading ? (
        <PageContainer>
          {data ? (
            <Fragment>
              <PageHeader>
                <PageTitle>{data.conversionGoal.title}</PageTitle>
              </PageHeader>
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>ゴールタイプ</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <StyledPaperItem>
                    <ContentText>
                      ゴールタイプ :{' '}
                      {data.conversionGoal.trigger && data.conversionGoal.trigger.type
                        ? getGoalType(data.conversionGoal.trigger.type, data.conversionGoal.transaction_type)
                        : null}
                    </ContentText>
                  </StyledPaperItem>
                  <StyledPaperItem>
                    {`作成日 : ${DateTime.fromISO(data.conversionGoal.created_at).toFormat(
                      'yyyy/MM/dd',
                    )} 最終更新日 : ${DateTime.fromISO(data.conversionGoal.updated_at).toFormat('yyyy/MM/dd')}`}
                  </StyledPaperItem>
                </PaperContent>
              </StyledPaper>
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>ゴール名と概要</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  <StyledPaperItem>ゴール名</StyledPaperItem>
                  <ContentText>{data.conversionGoal.title}</ContentText>
                  <StyledPaperItem>概要</StyledPaperItem>
                  <DescriptionText>{data.conversionGoal.description}</DescriptionText>
                  <PaperDivider />
                  <PaperFooter>
                    <PaperButton
                      onClick={() => {
                        setDrawerType('NAME');
                        setOpen(true);
                      }}
                    >
                      編集する
                    </PaperButton>
                  </PaperFooter>
                </PaperContent>
              </StyledPaper>
              <StyledPaper>
                <PaperTitle>
                  <PaperTitleText>コンバージョンタグ</PaperTitleText>
                </PaperTitle>
                <PaperContent>
                  {data.conversionGoal.goal_tags && data.conversionGoal.goal_tags?.length > 0 ? (
                    <StyledPaperItem>
                      {data.conversionGoal.goal_tags.map((tag) => {
                        return <StyledChip key={tag?.id} label={tag?.name} variant="outlined" />;
                      })}
                    </StyledPaperItem>
                  ) : (
                    <StyledPaperItem>現在選択されているタグはありません</StyledPaperItem>
                  )}
                  <PaperDivider />
                  <PaperFooter>
                    <PaperButton
                      onClick={() => {
                        setDrawerType('TAG');
                        setOpen(true);
                      }}
                    >
                      編集する
                    </PaperButton>
                  </PaperFooter>
                </PaperContent>
              </StyledPaper>
              {data.conversionGoal.trigger.type === 'click' ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>トラッキングコード</PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    <StyledPaperItem>
                      クリックを計測したいリンクやボタンに以下のコードを追記してください
                    </StyledPaperItem>
                    <StyledPaperItem>
                      <CodeBlock>{`data-kz-track-on-click="${data.conversionGoal.id}"`}</CodeBlock>
                    </StyledPaperItem>
                    <StyledPaperItem>設定例</StyledPaperItem>
                    <StyledPaperItem>
                      <CodeBlock>
                        {`<!-- link -->`}
                        <br />
                        {`<a href="..." data-kz-track-on-click="${data.conversionGoal.id}">この商品をカートに入れる</a>`}
                        <br />
                        {` <!-- form -->`}
                        <br />
                        {`<form action="...">`}
                        <br />
                        {`  <button type="submit" data-kz-track-on-click="${data.conversionGoal.id}">会員登録する</button>`}
                        <br />
                        {`</form>`}
                        <br />
                        {` <!-- any other element -->`}
                        <br />
                        {`<div data-kz-track-on-click="${data.conversionGoal.id}">資料を請求する</div>`}
                      </CodeBlock>
                    </StyledPaperItem>
                  </PaperContent>
                </StyledPaper>
              ) : null}
              {data.conversionGoal.trigger.type === 'transaction' &&
              data.conversionGoal.transaction_type === 'amount' ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>トラッキングコード</PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    <StyledPaperItem>総額情報を送信する場合</StyledPaperItem>
                    <StyledPaperItem>
                      <PreWrapDiv>
                        ECサイトの購入完了画面などで、購入金額を送信します。送信した金額データに基いて、ラウンドの計測をすることができます。
                        <br />
                        ウェブサイトの構造によっては、トランザクション完了画面がリロードされた時などに再度情報を送信しまうことがありますので、注文番号などお客様のサイトページ内のユニークな値をトランザクションごとにunique_idに設定することをお勧めします。
                        <br />
                        これにより、同一のトランザクションが複数回送信されるのを阻止することができます。
                        <br />
                        送信方法には、「単価 x
                        個数」の組合せを送るパターンと、「総額のみ」を送るパターンの2種類があります。
                      </PreWrapDiv>
                    </StyledPaperItem>
                    <StyledPaperItem>例 : 「単価 × 個数」の組み合わせを送るパターン</StyledPaperItem>
                    <StyledPaperItem>
                      <CodeBlock>
                        {`<script type="text/javascript">`}
                        <br />
                        {`  var kzsGoalId = ${data.conversionGoal.id};`}
                        <br />
                        {`  kzs('trackTransaction', kzsGoalId,`}
                        <br />
                        {`     {unit_price: 1.00, quantity: 3, unique_id: "49E-355444"},`}
                        <br />
                        {`     {unit_price: 4.99, quantity: 1, unique_id: "49E-355444"}`}
                        <br />
                        {`  );`}
                        <br />
                        {`</script>`}
                      </CodeBlock>
                    </StyledPaperItem>
                    <StyledPaperItem>例 : 「総額のみ」を送るパターン</StyledPaperItem>
                    <StyledPaperItem>
                      <CodeBlock>
                        {`<script type="text/javascript">`}
                        <br />
                        {`  var kzsGoalId = ${data.conversionGoal.id};`}
                        <br />
                        {`  kzs('trackTransaction', kzsGoalId,`}
                        <br />
                        {`      {total_amount: 594, unique_id: "49E-355444"}`}
                        <br />
                        {`  );`}
                        <br />
                        {`</script>`}
                      </CodeBlock>
                    </StyledPaperItem>
                  </PaperContent>
                </StyledPaper>
              ) : null}
              {data.conversionGoal.trigger.type === 'transaction' &&
              data.conversionGoal.transaction_type === 'quantity' ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>トラッキングコード</PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    <StyledPaperItem>数量情報のみ送信する場合</StyledPaperItem>
                    <StyledPaperItem>
                      資料請求完了画面などで、金額情報を含まず数量のみ計測したい場合に選択します。
                    </StyledPaperItem>
                    <StyledPaperItem>設定例</StyledPaperItem>
                    <StyledPaperItem>
                      <CodeBlock>
                        {`<script type="text/javascript">`}
                        <br />
                        {`  var kzsGoalId = ${data.conversionGoal.id};`}
                        <br />
                        {`  kzs('trackTransaction', kzsGoalId,`}
                        <br />
                        {`     {quantity: 3, unique_id: "49E-355444"}`}
                        <br />
                        {`  );`}
                        <br />
                        {`</script>`}
                      </CodeBlock>
                    </StyledPaperItem>
                  </PaperContent>
                </StyledPaper>
              ) : null}
              {data.conversionGoal.trigger.type === 'page_visit' || data.conversionGoal.trigger.type === 'event' ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>対象のURL</PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    <StyledPaperItem>トラッキング対象デバイス</StyledPaperItem>
                    <ContentText>{getDeviceType(data.conversionGoal.trigger.device_type)}</ContentText>
                    <StyledPaperItem>ゴールの対象とするページのURL</StyledPaperItem>
                    {data.conversionGoal.trigger.url_matchers ? (
                      // @ts-ignore
                      data.conversionGoal.trigger.url_matchers?.map((urlMatcher) => {
                        if (
                          urlMatcher?.is_inclusion &&
                          (urlMatcher?.match_type === 'regexp' || urlMatcher?.match_type === 'substring')
                        ) {
                          return (
                            <UrlMatcherWrapper key={urlMatcher?.id}>
                              <MatchTypeWrapper>
                                {urlMatcher?.match_type === 'regexp' ? ' (正規表現)' : ' (部分一致)'}
                              </MatchTypeWrapper>
                              <UrlLink>{urlMatcher?.expression}</UrlLink>
                            </UrlMatcherWrapper>
                          );
                        } else if (urlMatcher?.is_inclusion) {
                          return (
                            <StyledLink
                              key={urlMatcher?.id}
                              href={urlMatcher?.expression}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MatchTypeWrapper>
                                {urlMatcher?.match_type === 'simple'
                                  ? ' (簡易一致)'
                                  : urlMatcher?.match_type === 'exact'
                                  ? ' (完全一致)'
                                  : urlMatcher?.match_type === 'prefix'
                                  ? ' (前方一致)'
                                  : null}
                              </MatchTypeWrapper>
                              <UrlLink>{urlMatcher?.expression}</UrlLink>
                              <div></div>
                            </StyledLink>
                          );
                        }
                      })
                    ) : (
                      <ContentText>対象のURLが入力されていません</ContentText>
                    )}
                    <StyledPaperItem>上記の対象から除外するURL</StyledPaperItem>
                    {data.conversionGoal.trigger.url_matchers ? (
                      // @ts-ignore
                      data.conversionGoal.trigger.url_matchers?.map((urlMatcher) => {
                        if (
                          !urlMatcher?.is_inclusion &&
                          (urlMatcher?.match_type === 'regexp' || urlMatcher?.match_type === 'substring')
                        ) {
                          return (
                            <UrlMatcherWrapper key={urlMatcher?.id}>
                              <MatchTypeWrapper>
                                {urlMatcher?.match_type === 'regexp' ? ' (正規表現)' : ' (部分一致)'}
                              </MatchTypeWrapper>
                              <UrlLink>{urlMatcher?.expression}</UrlLink>
                            </UrlMatcherWrapper>
                          );
                        } else if (!urlMatcher?.is_inclusion) {
                          return (
                            <StyledLink
                              key={urlMatcher?.id}
                              href={urlMatcher?.expression}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MatchTypeWrapper>
                                {urlMatcher?.match_type === 'simple'
                                  ? ' (簡易一致)'
                                  : urlMatcher?.match_type === 'exact'
                                  ? ' (完全一致)'
                                  : urlMatcher?.match_type === 'prefix'
                                  ? ' (前方一致)'
                                  : null}
                              </MatchTypeWrapper>
                              <UrlLink>{urlMatcher?.expression}</UrlLink>
                              <div></div>
                            </StyledLink>
                          );
                        }
                      })
                    ) : (
                      <ContentText>対象のURLが入力されていません</ContentText>
                    )}
                    <PaperDivider />
                    <PaperFooter>
                      <PaperButton
                        onClick={() => {
                          setDrawerType('URL');
                          setOpen(true);
                        }}
                      >
                        編集する
                      </PaperButton>
                    </PaperFooter>
                  </PaperContent>
                </StyledPaper>
              ) : null}
              {data.conversionGoal.trigger.type === 'event' ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>
                      イベント定義の設定
                      <HelpLink href="https://support.kaizenplatform.net/hc/ja/articles/206384562" target="_blank">
                        設定方法の解説を読む
                      </HelpLink>
                    </PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    {data.conversionGoal.trigger.event_matchers ? (
                      data.conversionGoal.trigger.event_matchers.map((eventMatcher) => (
                        <EventWrapper key={eventMatcher?.id}>
                          <StyledPaperTopItem>イベント名</StyledPaperTopItem>
                          <ContentText>{eventMatcher?.data.name}</ContentText>
                          <StyledPaperItem>イベントタイプ</StyledPaperItem>
                          <ContentText>{eventMatcher?.data.event}</ContentText>
                          <StyledPaperItem>フィルタ条件</StyledPaperItem>
                          {eventMatcher?.data.conditions?.findIndex(
                            (condition) => condition?.comparator !== 'et_selector',
                          ) === -1 || eventMatcher?.data.conditions?.length === 0 ? (
                            <StyledPaperItem>イベントの定義がされていません</StyledPaperItem>
                          ) : (
                            // @ts-ignore
                            eventMatcher?.data.conditions?.map((condition, index) => {
                              if (condition?.args) {
                                return (
                                  <ContentText key={`filter-${condition?.method}-${index}`}>{`${getMethodType(
                                    condition?.method,
                                  )} : ${condition?.args?.[0]} ${
                                    condition?.method !== 'tagName'
                                      ? condition?.comparator === 'et_equal'
                                        ? ' (等しい)'
                                        : condition?.comparator === 'et_contain'
                                        ? ' (含む)'
                                        : ''
                                      : ''
                                  }`}</ContentText>
                                );
                              }
                            })
                          )}
                          <StyledPaperItem>祖先要素によるフィルタ</StyledPaperItem>
                          {eventMatcher?.data.conditions?.findIndex(
                            (condition) => condition?.comparator === 'et_selector',
                          ) === -1 || eventMatcher?.data.conditions?.length === 0 ? (
                            <StyledPaperItem>イベントの定義がされていません</StyledPaperItem>
                          ) : (
                            // @ts-ignore
                            eventMatcher?.data.conditions?.map((condition) => {
                              if (condition?.conditions?.length !== 0) {
                                return condition?.conditions?.map((value, index) => {
                                  return (
                                    <ContentText key={`etselector-${value?.method}-${index}`}>{`${getMethodType(
                                      value?.method,
                                    )} : ${value?.args?.[0]} ${
                                      value?.method !== 'tagName'
                                        ? value?.comparator === 'et_equal'
                                          ? ' (等しい)'
                                          : value?.comparator === 'et_contain'
                                          ? ' (含む)'
                                          : ''
                                        : ''
                                    }`}</ContentText>
                                  );
                                });
                              } else {
                                return <StyledPaperItem key="no-event">イベントの定義がされていません</StyledPaperItem>;
                              }
                            })
                          )}
                        </EventWrapper>
                      ))
                    ) : (
                      <StyledPaperItem>イベントの定義がされていません</StyledPaperItem>
                    )}
                    <PaperDivider />
                    <PaperFooter>
                      <PaperButton
                        onClick={() => {
                          setDrawerType('EVENT');
                          setOpen(true);
                        }}
                      >
                        編集する
                      </PaperButton>
                    </PaperFooter>
                  </PaperContent>
                </StyledPaper>
              ) : null}
              {data.conversionGoal.projects && data.conversionGoal.projects.length > 0 ? (
                <StyledPaper>
                  <PaperTitle>
                    <PaperTitleText>ゴールが利用されているエクスペリエンス一覧</PaperTitleText>
                  </PaperTitle>
                  <PaperContent>
                    {data.conversionGoal.projects?.map((project) => {
                      return (
                        <StyledLink href={`/engine/experiments/${project?.id}`} key={project?.id}>
                          <UrlLink>
                            {project?.title}{' '}
                            {project?.primary_goal_id === data.conversionGoal.id ? ' (メインゴール)' : null}
                          </UrlLink>
                        </StyledLink>
                      );
                    })}
                  </PaperContent>
                </StyledPaper>
              ) : null}
            </Fragment>
          ) : (
            <div>データ取得に失敗しました</div>
          )}
        </PageContainer>
      ) : (
        <StyledLoading />
      )}
    </div>
  );
};

const CodeBlock = styled('div')`
  overflow: auto;
  white-space: pre;
  background-color: ${COLOR_TEXT_MAIN};
  color: #2dd28d;
  padding: 24px;
  border-radius: 8px;
  line-height: 160%;
  font-size: 14px;
`;

const UrlLink = styled(Typography)`
  color: #006bd6;
  font-weight: 400;
  font-size: 14px;
`;

const ContentText = styled(Typography)`
  color: ${COLOR_TEXT_MAIN};
  font-weight: 400;
  font-size: 14px;
  margin: 8px 0px;
`;

const DescriptionText = styled(Typography)`
  color: ${COLOR_TEXT_MAIN};
  font-weight: 400;
  font-size: 14px;
  margin: 8px 0px;
  white-space: pre-line;
`;

const StyledPaperItem = styled(PaperItem)`
  color: ${COLOR_TEXT_MAIN};
`;

const StyledPaperTopItem = styled(PaperItem)`
  color: ${COLOR_TEXT_MAIN};
  padding: 0px;
`;

const StyledLoading = styled(Loading)(`
  margin: 24px;
  height: 240px;
`);

const EventWrapper = styled('div')`
  padding: 24px;
  background: #f5f6f7;
  border-radius: 8px;
  margin-top: 16px;
`;

const StyledChip = styled(Chip)`
  margin-right: 8px;
  margin-bottom: 8px;
  color: ${COLOR_TEXT_MAIN};
  font-weight: 700;
  border: 1.5px solid #e6e7e8;
  backgroundcolor: #f5f6f7;
`;

const PreWrapDiv = styled('div')`
  white-space: pre-wrap;
`;

const UrlMatcherWrapper = styled('div')`
  text-decoration: none;
  overflow-wrap: anywhere;
  margin: 8px 0px;
  display: flex;
  align-items: center;
`;

const StyledLink = styled('a')`
  text-decoration: none;
  overflow-wrap: anywhere;
  margin: 8px 0px;
  display: flex;
  align-items: center;
`;

const MatchTypeWrapper = styled('div')`
  text-decoration: none;
  overflow-wrap: anywhere;
  font-weight: 400;
  font-size: 14px;
  margin-right: 8px;
  color: ${COLOR_TEXT_MAIN};
`;

const HelpLink = styled('a')`
  text-decoration: none;
  margin-left: 12px;
  font-size: 10px;
  color: #006bd6;
`;

export default GoalSettingDetailPage;
